import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Building2, CheckCircle2, KeyRound } from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Card, Field, PageHeader } from "../ui";

type Building = { id: string; name: string };
type Unit = { id: string; unitNumber: string };

/**
 * Public self-serve onboarding form. New owners pick their building +
 * unit and supply their contact info; the request lands in an admin
 * review queue. On approval, the system emails them an invite code +
 * registration link. Anonymous endpoint — no auth required.
 */
export function ClaimUnitPage() {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [form, setForm] = useState({
    buildingId: "",
    unitId: "",
    claimantEmail: "",
    claimantFirstName: "",
    claimantLastName: "",
    claimantPhone: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<
    | { kind: "submitted" }
    | { kind: "duplicate" }
    | { kind: "already_owner" }
    | null
  >(null);

  useEffect(() => {
    void api
      .get<{ data: Building[] }>("/api/public/buildings")
      .then((r) => setBuildings(r.data))
      .catch(() => setBuildings([]));
  }, []);

  useEffect(() => {
    if (!form.buildingId) {
      setUnits([]);
      return;
    }
    void api
      .get<{ data: Unit[] }>(`/api/public/buildings/${form.buildingId}/units`)
      .then((r) => setUnits(r.data))
      .catch(() => setUnits([]));
  }, [form.buildingId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{
        data: unknown;
        duplicate?: boolean;
        alreadyOwner?: boolean;
      }>("/api/public/claim-unit", form);
      if (res.alreadyOwner) {
        setOutcome({ kind: "already_owner" });
      } else if (res.duplicate) {
        setOutcome({ kind: "duplicate" });
      } else {
        setOutcome({ kind: "submitted" });
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "error" in err.body
            ? String((err.body as { error: string }).error)
            : `HTTP ${err.status}`
          : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  if (outcome) {
    return (
      <div className="max-w-xl mx-auto">
        <PageHeader title="Claim a unit" />
        <Card>
          <div className="text-center py-6">
            <CheckCircle2
              size={36}
              className="text-status-approved-fg mx-auto mb-3"
            />
            {outcome.kind === "submitted" && (
              <>
                <h2 className="text-ink-800 mb-2">Claim submitted</h2>
                <p className="text-[13.5px] text-ink-600 leading-relaxed">
                  Property Management will review your claim and email you an
                  invite code at <strong>{form.claimantEmail}</strong> if
                  approved. You can close this tab.
                </p>
              </>
            )}
            {outcome.kind === "duplicate" && (
              <>
                <h2 className="text-ink-800 mb-2">Already on file</h2>
                <p className="text-[13.5px] text-ink-600 leading-relaxed">
                  We already have a pending claim for that unit + email.
                  Property Management will reach out at{" "}
                  <strong>{form.claimantEmail}</strong> when they've reviewed
                  it. No need to resubmit.
                </p>
              </>
            )}
            {outcome.kind === "already_owner" && (
              <>
                <h2 className="text-ink-800 mb-2">You already have access</h2>
                <p className="text-[13.5px] text-ink-600 leading-relaxed">
                  An account at <strong>{form.claimantEmail}</strong> is
                  already listed as an owner of this unit. Try signing in
                  instead.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 mt-4 px-3.5 py-2 rounded-md bg-brand-700 text-white text-[13px] no-underline hover:bg-brand-800 hover:no-underline"
                >
                  <KeyRound size={14} />
                  Sign in
                </Link>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <PageHeader
        title="Claim a unit"
        subtitle="New to The Elm at Clark? Request access to your unit. Property Management reviews each claim and emails you an invite code on approval."
      />
      <Card>
        <form onSubmit={submit} className="space-y-1">
          <Field label="Building">
            <select
              className="select"
              value={form.buildingId}
              onChange={(e) =>
                setForm({ ...form, buildingId: e.target.value, unitId: "" })
              }
              required
            >
              <option value="">Choose a building…</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Unit">
            <select
              className="select"
              value={form.unitId}
              onChange={(e) => setForm({ ...form, unitId: e.target.value })}
              required
              disabled={!form.buildingId || units.length === 0}
            >
              <option value="">
                {form.buildingId ? "Choose your unit…" : "Pick a building first"}
              </option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  Unit {u.unitNumber}
                </option>
              ))}
            </select>
          </Field>
          <div className="field-row">
            <Field label="First name">
              <input
                className="input"
                value={form.claimantFirstName}
                onChange={(e) =>
                  setForm({ ...form, claimantFirstName: e.target.value })
                }
                required
                minLength={1}
                maxLength={100}
              />
            </Field>
            <Field label="Last name">
              <input
                className="input"
                value={form.claimantLastName}
                onChange={(e) =>
                  setForm({ ...form, claimantLastName: e.target.value })
                }
                required
                minLength={1}
                maxLength={100}
              />
            </Field>
          </div>
          <Field
            label="Email"
            hint="The address Property Management will email your invite code to."
          >
            <input
              className="input"
              type="email"
              value={form.claimantEmail}
              onChange={(e) =>
                setForm({ ...form, claimantEmail: e.target.value })
              }
              required
              maxLength={255}
            />
          </Field>
          <Field label="Phone (optional)">
            <input
              className="input"
              value={form.claimantPhone}
              onChange={(e) =>
                setForm({ ...form, claimantPhone: e.target.value })
              }
              maxLength={50}
            />
          </Field>
          <Field
            label="Note for Property Management (optional)"
            hint="Anything that will help them confirm your claim — closing date, prior owner's name, etc."
          >
            <textarea
              className="textarea"
              rows={3}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              maxLength={2000}
            />
          </Field>

          {error && <div className="alert-error">{error}</div>}
          <Button
            variant="primary"
            type="submit"
            loading={busy}
            disabled={!form.buildingId || !form.unitId}
            className="w-full mt-2"
          >
            <Building2 size={15} />
            {busy ? "Submitting…" : "Submit claim"}
          </Button>
        </form>
      </Card>
      <p className="text-center text-sm text-ink-500 mt-4">
        Already have an invite code?{" "}
        <Link
          to="/register"
          className="font-medium text-brand-700 hover:text-brand-800"
        >
          Register here
        </Link>
        .
      </p>
    </div>
  );
}
